import { PositionInTheCompanyChoice } from "@dune/types";
import {
	Button,
	ControlledCheckbox,
	ControlledDropdown,
	ControlledInput,
	ControlledPhoneInput,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import { brokerUserCreationSubRolesLabels } from "@dune/vars";
import React from "react";
import { useAddUserMutation } from "../../../../../../api/mutations/societies.mutations";
import { useGetBrokerEntities } from "../../../../../../api/queries/societies.queries";
import { AddUserFormData, useAddUserForm } from "./useAddUserForm";

interface AddUserDialogProps {
	isOpen: boolean;
	onClose: () => void;
	societyId: number;
}

export const AddUserDialog: React.FC<AddUserDialogProps> = ({
	isOpen,
	onClose,
	societyId,
}) => {
	const { data: entities = [] } = useGetBrokerEntities(societyId);
	const {
		formState: { errors },
		control,
		handleSubmit,
		reset,
	} = useAddUserForm();

	const addUserMutation = useAddUserMutation(societyId);

	const onSubmit = async (data: AddUserFormData) => {
		addUserMutation.mutate(data, {
			onSuccess: () => {
				reset();
				onClose();
			},
		});
	};

	const entityOptions = React.useMemo(() => {
		if (!entities || entities.length === 0) return { "": "Aucune entité" };
		return entities.reduce(
			(acc, entity) => {
				acc[entity] = entity;
				return acc;
			},
			{ "": "Aucune entité" } as Record<string, string>,
		);
	}, [entities]);

	const hasEntities = entities && entities.length > 0;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle className="flex justify-start">
						Ajouter un Utilisateur
					</DialogTitle>
				</DialogHeader>
				<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
					<ControlledDropdown
						control={control}
						name="entity"
						label="Entité"
						options={entityOptions}
						error={errors.entity?.message}
						disabled={!hasEntities}
					/>
					{!hasEntities && (
						<span className="text-gray-500 text-sm -mt-2">
							Aucune entité n'est configurée pour ce broker
						</span>
					)}

					<div className="flex flex-row gap-4">
						<ControlledInput
							control={control}
							name="lastname"
							label="Nom"
							placeholder="Nom"
							error={errors.lastname?.message}
						/>
						<ControlledInput
							control={control}
							name="firstname"
							label="Prénom"
							placeholder="Prénom"
							error={errors.firstname?.message}
						/>
					</div>

					<ControlledInput
						control={control}
						name="email"
						label="Email"
						placeholder="Email"
						error={errors.email?.message}
					/>

					<div className="flex flex-row gap-4">
						<div className="flex flex-col items-start gap-3 w-full">
							<ControlledPhoneInput
								control={control}
								name="mobilePhone"
								label="Mobile"
								placeholder="Mobile"
								defaultCountry="FR"
								error={errors.mobilePhone?.message}
							/>
						</div>
						<div className="flex flex-col items-start gap-3 w-full">
							<ControlledPhoneInput
								control={control}
								name="landlinePhone"
								label="Tel"
								placeholder="Téléphone fixe"
								defaultCountry="FR"
								error={errors.landlinePhone?.message}
							/>
						</div>
					</div>

					<ControlledDropdown
						control={control}
						name="positionInTheCompany"
						label="Fonction"
						options={PositionInTheCompanyChoice}
						error={errors.positionInTheCompany?.message}
					/>

					<ControlledDropdown
						control={control}
						name="subrole"
						label="Profile"
						options={brokerUserCreationSubRolesLabels}
						error={errors.subrole?.message}
					/>

					<ControlledCheckbox
						control={control}
						name="isSignatory"
						label="Utilisateur signataire"
						error={errors.isSignatory?.message}
					/>

					<div className="grid grid-cols-2 gap-3 pt-6">
						<Button className="w-full" variant="outline" onClick={onClose}>
							Annuler
						</Button>
						<Button
							className="w-full"
							type="submit"
							isLoading={addUserMutation.isPending}
						>
							Ajouter
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
};
